/**
 * Utility functions for handling redirect URLs with security validation
 */

/**
 * Dummy origin used only to resolve candidates with the same WHATWG parser the
 * browser uses. If a value resolves anywhere else, it was never a same-site
 * relative path — however much it looked like one.
 */
const RELATIVE_BASE = "https://relative.invalid";

/**
 * Normalizes a candidate redirect target to a same-site relative path.
 *
 * Prefix checks alone are not enough here: the browser normalizes backslashes
 * and strips tabs/newlines before resolving, so `/\evil.com`, `/\t/evil.com`,
 * `/\n/evil.com`, `/\r/evil.com` and `/\/evil.com` all navigate off-site while
 * looking like plain relative paths. Resolving with the same parser the browser
 * uses, then checking the origin, closes the whole family at once.
 *
 * **The output is validated too, not just the input.** Path normalization can
 * turn a same-origin input into something that no longer is: `/..//evil.com`
 * resolves on-origin — the `..` pops nothing — but leaves a `pathname` of
 * `//evil.com`, which the browser then reads as protocol-relative. Re-resolving
 * the string we are about to hand back is what closes that: the only value that
 * survives is one that means the same thing on the way out as on the way in.
 *
 * @returns the canonical path, or `null` if the value would leave the site.
 */
const toSafeRelativePath = (path: string): string | null => {
  if (!path.startsWith("/")) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(path, RELATIVE_BASE);
  } catch {
    return null;
  }

  if (url.origin !== RELATIVE_BASE) {
    return null;
  }

  // Return what the parser produced, never the raw input: the browser must not
  // be able to read the string differently from how we validated it.
  const canonical = `${url.pathname}${url.search}${url.hash}`;

  try {
    if (new URL(canonical, RELATIVE_BASE).origin !== RELATIVE_BASE) {
      return null;
    }
  } catch {
    return null;
  }

  return canonical;
};

/**
 * Safely decodes and validates a redirect URL from query parameters
 * @param nextParam - The raw `next` parameter from the URL query string
 * @param fallback - Default path to use if validation fails or param is missing
 * @returns A safe relative path for redirect
 */
export const getSafeRedirectPath = (
  nextParam: string | null | undefined,
  fallback = "/",
): string => {
  // The fallback is caller-supplied and can itself come from the query string
  // (e.g. `getSafeRedirectPath(next, callbackUrl)`), so it gets the same check.
  const safeFallback = toSafeRelativePath(fallback) ?? "/";

  if (!nextParam) {
    return safeFallback;
  }

  // A destination containing a bare `%` is not an attack, it is a URL — and
  // throwing the whole value away on it was visible in the product: the
  // destination collapsed to `/`, `carriableDestination` read that as "nothing
  // to carry", and the CTA that depended on it disappeared with no error
  // anywhere. Validating the raw value instead is not weaker: whatever reaches
  // `toSafeRelativePath` still has to survive the browser's own parser.
  let decoded: string;
  try {
    decoded = decodeURIComponent(nextParam);
  } catch {
    decoded = nextParam;
  }

  return toSafeRelativePath(decoded) ?? safeFallback;
};

/**
 * Builds a login URL with a next parameter for redirect after authentication
 * @param targetPath - The path to redirect to after login
 * @returns Login URL with next parameter
 */
export const buildLoginUrlWithNext = (targetPath: string): string => {
  const safePath = toSafeRelativePath(targetPath) ?? "/";
  return `/auth?next=${encodeURIComponent(safePath)}`;
};

/**
 * Builds the logout URL carrying the page the visitor was on.
 *
 * Why it exists: the refresh token lives seven days. Anyone who has not opened
 * the app for a week and clicks a shared profile link gets a 401 on
 * `/session/bootstrap`, the interceptor sends them to `/auth/logout`, and the
 * page they actually wanted is gone — replaced by a login screen they did not
 * need, since `/profile/:id` is public. Carrying the origin lets `LogoutRoute`
 * put them back where they were, now as an anonymous visitor.
 *
 * Paths under `/auth` are deliberately **not** carried: coming back to
 * `/auth/logout` after a logout is a loop, and the rest of `/auth` is where a
 * logged-out visitor already belongs. Anything that would leave the site is
 * dropped by the same validation as every other redirect here.
 */
export const buildLogoutUrlWithNext = (targetPath: string): string => {
  const safePath = toSafeRelativePath(targetPath);
  if (!safePath) return "/auth/logout";

  // Sul **pathname**, non sulla stringa intera: `/auth?next=…` è una rotta di
  // autenticazione tanto quanto `/auth`, e un confronto letterale la lascerebbe
  // passare.
  const pathname = safePath.split(/[?#]/)[0];
  if (pathname === "/auth" || pathname?.startsWith("/auth/")) {
    return "/auth/logout";
  }

  return `/auth/logout?next=${encodeURIComponent(safePath)}`;
};
